"use client";

import React, { useState } from "react";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
export default function EstimateRequestForm() {
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [projectType] = useState("Interior");
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setZipError("");
    setConsentError("");
    setErrorMessage("");
    let hasError = false;

    // Validate 5-digit ZIP code
    if (!/^\d{5}$/.test(zip.trim())) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      hasError = true;
    }

    // Validate Consent checkbox
    if (!consent) {
      setConsentError("Please agree to be contacted to proceed.");
      hasError = true;
    }
    if (hasError) {
      return;
    }
    setSubmitting(true);
    try {
      const payload = new URLSearchParams({
        name: fullName,
        phone,
        email,
        project_type: projectType,
        address: zip,
        zip,
        details: message,
        timing,
        budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: typeof window !== "undefined" ? window.location.href : "",
        form_location: "interior-remodeling-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: payload
      });
      if (response.ok) {
        setSubmitSuccess(true);
      } else {
        setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    } finally {
      setSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="rounded-2xl border border-border bg-card/85 backdrop-blur-md shadow-xl p-6 sm:p-10 transition-all duration-250 ease-out">
          <header className="mb-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight text-foreground">
              Request an Estimate
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground font-default max-w-xl mx-auto">
              Share details about your upcoming interior renovation to receive a personalized consultation and estimate.
            </p>
          </header>

          {submitSuccess ? <div className="py-12 px-4 text-center transition-opacity duration-250 ease-out">
              <div className="inline-flex items-center justify-center size-16 rounded-full bg-primary/20 text-primary mb-5">
                <CheckCircle2 className="size-8" />
              </div>
              <h3 className="text-2xl font-serif font-medium text-foreground mb-2">
                Estimate Request Received
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base max-w-md mx-auto">
                Thank you for reaching out. A Brookhaven Elevation Renovations specialist will review your project details and contact you shortly.
              </p>
            </div> : <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {errorMessage && <Alert variant="destructive" className="transition-all duration-250 ease-out border-destructive/50">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Unable to Send Request</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="fullName">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="fullName" type="text" required value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Jane Doe" disabled={submitting} />
                </div>

                {/* Phone Number */}
                <div className="space-y-2">
                  <Label htmlFor="phone">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input id="phone" type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="(404) 555-0123" disabled={submitting} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Email Address */}
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com" disabled={submitting} />
                </div>

                {/* ZIP Code */}
                <div className="space-y-2">
                  <Label htmlFor="zip">
                    ZIP Code <span className="text-destructive">*</span>
                  </Label>
                  <Input id="zip" type="text" required maxLength={5} value={zip} onChange={e => {
                setZip(e.target.value);
                if (zipError) setZipError("");
              }} placeholder="30319" aria-invalid={Boolean(zipError)} disabled={submitting} />
                  {zipError && <p className="text-xs text-destructive font-medium mt-1">
                      {zipError}
                    </p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {/* Project Type */}
                <div className="space-y-2">
                  <Label htmlFor="projectType">
                    Project Type <span className="text-destructive">*</span>
                  </Label>
                  <select id="projectType" name="project_type" value={projectType} disabled className="flex h-9 w-full rounded-md border border-input bg-muted px-3 py-1 text-sm shadow-xs transition-colors text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-75">
                    <option value="Kitchen">Kitchen</option>
                    <option value="Bathroom">Bathroom</option>
                    <option value="Whole-Home">Whole-Home</option>
                    <option value="Home Addition">Home Addition</option>
                    <option value="Basement">Basement</option>
                    <option value="Interior">Interior</option>
                    <option value="Outdoor Living">Outdoor Living</option>
                    <option value="Deck">Deck</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Preferred Timing */}
                <div className="space-y-2">
                  <Label htmlFor="timing">Preferred Timing</Label>
                  <select id="timing" value={timing} onChange={e => setTiming(e.target.value)} disabled={submitting} className="flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-sm shadow-xs transition-colors text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                    <option value="">Select timeframe</option>
                    <option value="As soon as possible">As soon as possible</option>
                    <option value="1-3 months">1-3 months</option>
                    <option value="3-6 months">3-6 months</option>
                    <option value="6+ months / just exploring">6+ months / just exploring</option>
                  </select>
                </div>

                {/* Budget Range */}
                <div className="space-y-2">
                  <Label htmlFor="budget">Budget Range</Label>
                  <select id="budget" value={budget} onChange={e => setBudget(e.target.value)} disabled={submitting} className="flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-sm shadow-xs transition-colors text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                    <option value="">Select range</option>
                    <option value="Under $25,000">Under $25,000</option>
                    <option value="$25,000-$75,000">$25,000-$75,000</option>
                    <option value="$75,000-$150,000">$75,000-$150,000</option>
                    <option value="$150,000-$300,000">$150,000-$300,000</option>
                    <option value="$300,000+">$300,000+</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>
              </div>

              {/* Project Details / Message */}
              <div className="space-y-2">
                <Label htmlFor="message">Project Details / Message</Label>
                <Textarea id="message" value={message} onChange={e => setMessage(e.target.value)} placeholder="Tell us about the scope, specific interior rooms, preferred materials, or design inspirations..." rows={4} disabled={submitting} />
              </div>

              {/* Required Consent Checkbox */}
              <div className="pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox id="consent" checked={consent} onCheckedChange={checked => {
                setConsent(Boolean(checked));
                if (consentError) setConsentError("");
              }} disabled={submitting} className="mt-0.5" aria-invalid={Boolean(consentError)} />
                  <div className="grid gap-1.5 leading-none">
                    <Label htmlFor="consent" className="text-sm font-normal text-muted-foreground cursor-pointer leading-relaxed">
                      I agree to be contacted about my project by phone, text, or email.
                    </Label>
                    {consentError && <p className="text-xs text-destructive font-medium">
                        {consentError}
                      </p>}
                  </div>
                </div>
              </div>

              {/* Submit CTA */}
              <div className="pt-4">
                <Button type="submit" disabled={submitting} size="lg" className="w-full sm:w-auto font-medium px-8">
                  {submitting ? <>
                      <Loader2 className="size-4 animate-spin" />
                      Submitting Request...
                    </> : "Submit Estimate Request"}
                </Button>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}